import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTabs, markOpenNewTab, consumeOpenNewTab, markTabSwitchNav, saveScrollFor, getSearchFor } from '../context/TabsContext';
import { X, Minus, Maximize2, Minimize2, Pin, PinOff, XSquare, Plus, Search, MoreVertical, Menu as MenuIcon, ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuSeparator,
} from './ui/context-menu';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useStickyHeaderVar } from '../hooks/useStickyHeaderVar';

const OVERRIDES = {
  '/staff/dashboard': 'Dashboard',
  '/staff/mobile-balances': 'Mobile Balances',
  '/staff/manual': 'Operation Manual',
  '/staff/loans-hub': 'Loans Hub',
  '/staff/customers': 'Customers',
  '/staff/expenses': 'Expenses',
  '/staff/potential-customers/new': 'New Potential Customer',
  '/staff/marketing/plan/new': 'New Marketing Plan',
  '/staff/loans/reconciliation': 'Dedukt Reconciliation',
};

// Dynamic (param-based) routes don't have a fixed path key — match by
// pattern so tabs show a human label instead of the raw id segment.
const PATTERN_TITLES = [
  [/^\/staff\/new-tab\/.+$/, 'New Tab'],
  [/^\/staff\/marketing\/report\/new\/.+$/, 'New Execution Report'],
  [/^\/staff\/marketing\/report\/(?!new\/).+$/, 'Execution Report'],
  [/^\/staff\/marketing\/plan\/.+\/edit$/, 'Edit Marketing Plan'],
  [/^\/staff\/potential-customers\/(?!new$).+$/, 'Potential Customer'],
  [/^\/staff\/customers\/[^/]+\/transactions$/, 'Customer Transactions'],
  [/^\/staff\/customers\/[^/]+$/, 'Customer Profile'],
  [/^\/staff\/loans\/(?!reconciliation(\/|$)).+$/, 'Loan Details'],
  [/^\/staff\/team\/[^/]+$/, 'Staff Profile'],
  [/^\/staff\/refunds\/alerts\/.+$/, 'Refund Alert'],
  [/^\/staff\/applications\/[^/]+$/, 'Loan Application'],
  [/^\/staff\/recruitment\/applications\/.+$/, 'Recruitment Application'],
  [/^\/admin\/staff-loans\/history\/.+$/, 'Staff Loan History'],
  [/^\/staff\/loans\/reconciliation\/.+$/, 'Loan Reconciliation'],
];

const titleFor = (path) => {
  if (OVERRIDES[path]) return OVERRIDES[path];
  const pattern = PATTERN_TITLES.find(([rx]) => rx.test(path));
  if (pattern) {
    // Append a short id suffix so multiple open records of the same type
    // (e.g. two "Customer Profile" tabs) are distinguishable at a glance.
    const lastSeg = path.split('?')[0].split('/').filter(Boolean).pop() || '';
    const suffix = lastSeg.replace(/[^a-zA-Z0-9]/g, '').slice(-4);
    return suffix ? `${pattern[1]} · ${suffix}` : pattern[1];
  }
  const seg = (path.split('?')[0].split('/').filter(Boolean).pop() || 'page').replace(/-/g, ' ');
  return seg.replace(/\b\w/g, (c) => c.toUpperCase());
};

export default function HeaderTabsBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const barRef = useRef(null);
  useStickyHeaderVar(barRef, '--bbc-tabs-bar-h');
  const {
    tabs, minimized, pinned, maximized,
    addTab, closeTab, minimizeTab, restoreTab, togglePinTab, reorderTabs, replaceTab, toggleMaximize, consumeLastPath,
  } = useTabs();
  const active = location.pathname;
  const isChatPage = active.includes('/chat');
  const [dragPath, setDragPath] = useState(null);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const tabRefs = useRef(new Map());
  // Keep a ref mirror of `tabs` so the nav-decision effect below always
  // reads the latest tabs even though it isn't in its dependency array
  // (it only needs to run when `active` changes, not on every tabs update).
  const tabsRef = useRef(tabs);
  useEffect(() => { tabsRef.current = tabs; }, [tabs]);
  // Same pattern for `pinned` — needed below so a stray POP navigation
  // (e.g. browser Back landing on a URL that used to be its own tab
  // before it was closed, but is still further back in shared browser
  // history) can never rebind a PINNED tab out from under the user.
  const pinnedRef = useRef(pinned);
  useEffect(() => { pinnedRef.current = pinned; }, [pinned]);

  // Browser-like tabs: navigating anywhere (sidebar clicks, links, swipe,
  // back) updates the tab that's CURRENTLY active in place — it never
  // auto-opens a new one. The only way to get an extra tab is the explicit
  // "+" button below, which flags the very next navigation via
  // markOpenNewTab() right before calling navigate().
  useEffect(() => {
    if (!active.startsWith('/staff') && !active.startsWith('/admin')) return;
    // Always consume both (never skip either) so nothing leaks into a
    // later, unrelated navigation.
    const wantsNewTab = consumeOpenNewTab();
    const from = consumeLastPath(active);
    const currentTabs = tabsRef.current;
    const alreadyHasTab = currentTabs.some((t) => t.path === active);
    if (!alreadyHasTab) {
      // Never rebind a PINNED tab: a pinned tab is a deliberately kept-
      // open page. A tab that got closed can still be reachable via a
      // stray browser Back (its URL stays on the shared history stack)
      // — treat that resurrection as opening its own tab again, not as
      // hijacking whatever pinned tab happens to be active right now.
      if (!wantsNewTab && from && from !== active && currentTabs.some((t) => t.path === from) && !pinnedRef.current.includes(from)) {
        replaceTab(from, active, titleFor(active));
      } else {
        addTab(active, titleFor(active));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, addTab, replaceTab, consumeLastPath]);

  const openTabs = tabs.filter((t) => !minimized.includes(t.path));
  const minTabs = tabs.filter((t) => minimized.includes(t.path));

  const handleNewTab = () => {
    saveScrollFor(active); // remember where the tab we're leaving was
    markOpenNewTab();
    navigate(`/staff/new-tab/${Date.now().toString(36)}`);
  };

  // Mobile: keep the active tab centered in the scrollable tab strip.
  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    if (!isMobile) return;
    const el = tabRefs.current.get(active);
    if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [active, openTabs.length]);

  const setTabRef = useCallback((path, el) => {
    if (el) tabRefs.current.set(path, el);
    else tabRefs.current.delete(path);
  }, []);

  const handleClose = (e, path) => {
    e.stopPropagation();
    closeTab(path);
    if (path === active) {
      const remaining = tabs.filter((t) => t.path !== path && !minimized.includes(t.path));
      markTabSwitchNav();
      const next = remaining.length ? remaining[remaining.length - 1].path : '/staff/dashboard'; navigate(next + getSearchFor(next), { replace: true });
    }
  };

  const handleCloseOthers = (path) => {
    tabs.forEach((t) => {
      if (t.path !== path && !pinned.includes(t.path)) closeTab(t.path);
    });
    if (active !== path) {
      markTabSwitchNav();
      navigate(path + getSearchFor(path), { replace: true });
    }
  };

  const onDrop = (e, overPath) => {
    e.preventDefault();
    if (dragPath && dragPath !== overPath) reorderTabs(dragPath, overPath);
    setDragPath(null);
  };

  // Move a tab one slot left/right among the OPEN tabs (works on touch via the long-press menu or the hover arrows).
  const moveTab = (path, dir) => {
    const idx = openTabs.findIndex((t) => t.path === path);
    const target = openTabs[idx + dir];
    if (idx === -1 || !target) return;
    reorderTabs(path, target.path);
  };

  if (tabs.length === 0) return null;

  return (
    <div
      ref={barRef}
      className={cn(
        "border-b border-border bg-forest-50",
        (maximized || !isChatPage) && "sticky top-0",
        maximized ? "z-40" : "z-30"
      )}
      data-testid="header-tabs-bar">
      <div className="flex items-stretch">
        <div className={`flex-1 flex items-center gap-1 overflow-x-auto py-1.5 scrollbar-thin ${maximized ? 'pl-12 pr-2' : 'px-2'}`}>
          {/* Mobile: the page header scrolls away but this strip stays — so the side-menu button lives here too */}
          <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('bbc:open-sidebar'))} className="lg:hidden shrink-0 mr-1 p-1.5 rounded-md text-forest-700 hover:bg-forest-100" aria-label="Open menu" data-testid="tabs-sidebar-toggle">
            <MenuIcon className="w-5 h-5" />
          </button>
          {openTabs.length === 0 && <span className="text-xs text-muted-foreground px-2">All tabs minimised — restore below</span>}
          {openTabs.map((t, idx) => {
            const isActive = t.path === active;
            const isPinned = pinned.includes(t.path);
            const canLeft = idx > 0, canRight = idx < openTabs.length - 1;
            return (
              <ContextMenu key={t.path}>
                <ContextMenuTrigger asChild>
                  <div
                    ref={(el) => setTabRef(t.path, el)}
                    draggable
                    onDragStart={() => setDragPath(t.path)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => onDrop(e, t.path)}
                    onClick={() => { markTabSwitchNav(); navigate(t.path + getSearchFor(t.path), { replace: true }); }}
                    className={`group flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-t-md cursor-pointer text-xs whitespace-nowrap border-t border-x transition-colors ${isActive ? 'bg-white border-forest-200 text-forest-900 font-semibold shadow-sm' : 'bg-forest-100/60 border-transparent text-forest-700 hover:bg-forest-100'} ${dragPath === t.path ? 'opacity-50' : ''}`}
                    data-testid={`header-tab-${t.path}`}
                    title={t.title}
                  >
                    {isPinned && <Pin className="w-3 h-3 text-forest-500 shrink-0" data-testid={`tab-pinned-icon-${t.path}`} />}
                    {isActive && canLeft && (
                      <button onClick={(e) => { e.stopPropagation(); moveTab(t.path, -1); }} className="p-0.5 rounded hover:bg-forest-200 text-forest-500" title="Move tab left" data-testid={`tab-move-left-${t.path}`}>
                        <ArrowLeft className="w-3 h-3" />
                      </button>
                    )}
                    <span className="max-w-[140px] truncate">{t.title}</span>
                    {isActive && canRight && (
                      <button onClick={(e) => { e.stopPropagation(); moveTab(t.path, 1); }} className="p-0.5 rounded hover:bg-forest-200 text-forest-500" title="Move tab right" data-testid={`tab-move-right-${t.path}`}>
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); minimizeTab(t.path); }} className="p-0.5 rounded hover:bg-forest-200 text-forest-500" title="Minimise" data-testid={`tab-minimize-${t.path}`}>
                      <Minus className="w-3 h-3" />
                    </button>
                  </div>
                </ContextMenuTrigger>
                <ContextMenuContent data-testid={`tab-context-menu-${t.path}`}>
                  <ContextMenuItem onClick={() => togglePinTab(t.path)} data-testid={`tab-context-pin-${t.path}`}>
                    {isPinned ? <PinOff className="w-3.5 h-3.5 mr-2" /> : <Pin className="w-3.5 h-3.5 mr-2" />}
                    {isPinned ? 'Unpin tab' : 'Pin tab'}
                  </ContextMenuItem>
                  <ContextMenuItem onClick={() => minimizeTab(t.path)} data-testid={`tab-context-minimize-${t.path}`}>
                    <Minus className="w-3.5 h-3.5 mr-2" /> Minimise
                  </ContextMenuItem>
                  <ContextMenuSeparator />
                  <ContextMenuItem disabled={!canLeft} onClick={() => moveTab(t.path, -1)} data-testid={`tab-context-move-left-${t.path}`}>
                    <ArrowLeft className="w-3.5 h-3.5 mr-2" /> Move left
                  </ContextMenuItem>
                  <ContextMenuItem disabled={!canRight} onClick={() => moveTab(t.path, 1)} data-testid={`tab-context-move-right-${t.path}`}>
                    <ArrowRight className="w-3.5 h-3.5 mr-2" /> Move right
                  </ContextMenuItem>
                  <ContextMenuSeparator />
                  <ContextMenuItem onClick={() => handleCloseOthers(t.path)} data-testid={`tab-context-close-others-${t.path}`}>
                    <XSquare className="w-3.5 h-3.5 mr-2" /> Close other tabs
                  </ContextMenuItem>
                  <ContextMenuItem onClick={(e) => handleClose(e, t.path)} data-testid={`tab-context-close-${t.path}`}>
                    <X className="w-3.5 h-3.5 mr-2" /> Close tab
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            );
          })}
        </div>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('bbc:global-search-open'))}
          className="hidden sm:flex shrink-0 px-2.5 items-center text-forest-600 hover:bg-forest-100 hover:text-forest-800 border-l border-border"
          title="Jump to a tab or search (Ctrl/Cmd+K)"
          data-testid="tabs-search-btn"
        >
          <Search className="w-4 h-4" />
        </button>
        <button
          onClick={handleNewTab}
          className="hidden sm:flex shrink-0 px-2.5 items-center text-forest-600 hover:bg-forest-100 hover:text-forest-800 border-l border-border"
          title="New tab"
          data-testid="tabs-new-tab-btn"
        >
          <Plus className="w-4 h-4" />
        </button>
        <button
          onClick={toggleMaximize}
          className="hidden sm:flex shrink-0 px-3 items-center gap-1.5 text-xs text-forest-700 hover:bg-forest-100 border-l border-border"
          title={maximized ? 'Restore view' : 'Maximise (focus mode)'}
          data-testid="tabs-maximize-toggle"
        >
          {maximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          <span className="hidden sm:inline">{maximized ? 'Restore' : 'Maximise'}</span>
        </button>

        {/* Mobile: Search / New tab / Maximise collapse into one button that
            expands on touch, instead of eating scarce tab-strip width. */}
        <Popover open={mobileActionsOpen} onOpenChange={setMobileActionsOpen}>
          <PopoverTrigger asChild>
            <button
              className="flex sm:hidden shrink-0 px-2.5 items-center text-forest-600 hover:bg-forest-100 hover:text-forest-800 border-l border-border"
              title="Tab actions"
              data-testid="tabs-mobile-actions-btn"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" collisionPadding={12} className="w-40 p-1" data-testid="tabs-mobile-actions-menu">
            <button
              onClick={() => { setMobileActionsOpen(false); window.dispatchEvent(new CustomEvent('bbc:global-search-open')); }}
              className="w-full flex items-center gap-2 px-2 py-2 rounded text-sm text-forest-700 hover:bg-forest-100"
              data-testid="tabs-search-btn-mobile"
            >
              <Search className="w-4 h-4" /> Search tabs
            </button>
            <button
              onClick={() => { setMobileActionsOpen(false); handleNewTab(); }}
              className="w-full flex items-center gap-2 px-2 py-2 rounded text-sm text-forest-700 hover:bg-forest-100"
              data-testid="tabs-new-tab-btn-mobile"
            >
              <Plus className="w-4 h-4" /> New tab
            </button>
            <button
              onClick={() => { setMobileActionsOpen(false); toggleMaximize(); }}
              className="w-full flex items-center gap-2 px-2 py-2 rounded text-sm text-forest-700 hover:bg-forest-100"
              data-testid="tabs-maximize-btn-mobile"
            >
              {maximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              {maximized ? 'Restore' : 'Maximise'}
            </button>
          </PopoverContent>
        </Popover>
      </div>

      {minTabs.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap px-3 py-1.5 border-t border-border/60 bg-white/60" data-testid="minimized-tabs-row">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground mr-1">Minimised</span>
          {minTabs.map((t) => (
            <button
              key={t.path}
              onClick={() => { restoreTab(t.path); markTabSwitchNav(); navigate(t.path + getSearchFor(t.path), { replace: true }); }}
              className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-forest-100 text-forest-700 hover:bg-forest-200"
              data-testid={`minimized-chip-${t.path}`}
            >
              {pinned.includes(t.path) && <Pin className="w-3 h-3 text-forest-500" />}
              <span className="max-w-[120px] truncate">{t.title}</span>
              <span onClick={(e) => { e.stopPropagation(); closeTab(t.path); }} className="hover:text-red-600" data-testid={`min-close-${t.path}`}><X className="w-3 h-3" /></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
